import styled, { css } from 'styled-components';

const interactiveUploadSurface = css`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #fafafa;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;

  &:hover {
    border-color: #448181;
    background-color: rgba(68, 129, 129, 0.05);
  }
`;

export const PageContainer = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  box-sizing: border-box;
  gap: 32px;
  text-align: left;
  font-size: 28px;
  color: #171719;
  font-family: 'Pretendard JP', sans-serif;
`;

export const TitleSection = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 16px;
`;

export const PageTitle = styled.h1`
  position: relative;
  letter-spacing: -0.02em;
  line-height: 135.8%;
  font-weight: 700;
  margin: 0;
`;

export const MessageBox = styled.div<{ $variant: 'success' | 'error' }>`
  background-color: ${({ $variant }) => ($variant === 'success' ? '#38a169' : '#e53e3e')};
  color: #fff;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  text-align: center;
  align-self: stretch;
  font-size: 14px;
`;

export const Form = styled.form<{ $loading: boolean }>`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 32px;
  font-size: 14px;
  color: rgba(46, 47, 51, 0.88);
  opacity: ${({ $loading }) => ($loading ? 0.7 : 1)};
  pointer-events: ${({ $loading }) => ($loading ? 'none' : 'auto')};
`;

export const FormRow = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const InputGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
`;

export const FullWidthSection = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
`;

export const Label = styled.label<{ $required?: boolean }>`
  position: relative;
  letter-spacing: 0.01em;
  line-height: 142.9%;
  font-weight: 600;
  color: rgba(46, 47, 51, 0.88);

  ${({ $required }) =>
    $required &&
    css`
      &::after {
        content: ' *';
        color: #e53e3e;
      }
    `}
`;

export const InputWrapper = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  font-size: 16px;
  color: rgba(55, 56, 60, 0.28);
`;

export const InputBox = styled.div`
  flex: 1;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  background-color: #fff;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  position: relative;
  gap: 12px;

  &:focus-within {
    border-color: #448181;
    box-shadow: 0 0 0 3px rgba(68, 129, 129, 0.1);
  }
`;

const baseTextField = css`
  flex: 1;
  border: none;
  outline: none;
  font-family: inherit;
  font-size: 16px;
  color: #171719;
  background: transparent;

  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
`;

export const TextInput = styled.input`
  ${baseTextField}
`;

export const TagInput = styled.input`
  ${baseTextField}
  min-width: 200px;
  padding: 0;
`;

export const SelectBox = styled.div`
  flex: 1;
  border-radius: 12px;
  background-color: #f4f4f5;
  border: 1px solid rgba(112, 115, 124, 0.08);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 12px;
  position: relative;
  gap: 12px;
`;

export const SelectInput = styled.select`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 16px;
  color: rgba(55, 56, 60, 0.16);
  cursor: pointer;
`;

export const SelectIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const HelperText = styled.div`
  font-size: 12px;
  color: rgba(55, 56, 60, 0.61);
  margin-top: 4px;
  line-height: 1.4;
`;

export const ErrorText = styled.div`
  color: #e53e3e;
  font-size: 12px;
  margin-top: 4px;
`;

export const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  width: 100%;
  min-height: 40px;
`;

export const Tag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #448181;
  color: #fff;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background-color: #3a6f6f;
  }
`;

export const TagText = styled.span`
  line-height: 1;
`;

export const TagRemoveButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.2);
  }
`;

export const ImageUploadSection = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 16px;
`;

export const ThumbnailGroup = styled.div`
  align-self: stretch;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
`;

export const ImagePreview = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 24px;
`;

export const ThumbnailUpload = styled.div`
  ${interactiveUploadSurface}
  width: 271px;
  height: 180px;
`;

export const BannerUpload = styled.div`
  ${interactiveUploadSurface}
  flex: 1;
  height: 180px;
`;

export const IntroductionUpload = styled.div`
  ${interactiveUploadSurface}
  flex: 1;
  height: 200px;
`;

export const UploadPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: rgba(55, 56, 60, 0.61);
  width: 100%;
  height: 100%;
  transition: all 0.2s ease;

  ${ThumbnailUpload}:hover &,
  ${BannerUpload}:hover &,
  ${IntroductionUpload}:hover & {
    color: #448181;
  }
`;

export const UploadIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  ${ThumbnailUpload}:hover &,
  ${BannerUpload}:hover &,
  ${IntroductionUpload}:hover & {
    transform: scale(1.1);
  }
`;

export const UploadText = styled.div`
  font-size: 14px;
  text-align: center;
  font-weight: 500;
`;

export const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
`;

export const HiddenInput = styled.input`
  display: none;
`;

export const Actions = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 24px;
  text-align: center;
  font-size: 16px;
  color: rgba(55, 56, 60, 0.61);
`;

export const ActionButtons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 24px;
`;

export const SaveDraftButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 4px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: rgba(55, 56, 60, 0.61);
`;

export const ButtonContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

export const ButtonLabel = styled.div`
  position: relative;
  letter-spacing: 0.01em;
  line-height: 150%;
  font-weight: 600;
`;

export const SubmitButtonWrap = styled.div`
  width: 160px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
`;

export const SubmitButton = styled.button`
  align-self: stretch;
  border-radius: 12px;
  background-color: #448181;
  border: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 28px;
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s ease;
  color: #fff;

  &:hover {
    background-color: #3a6f6f;
  }

  &:disabled {
    background-color: rgba(112, 115, 124, 0.28);
    cursor: not-allowed;
  }
`;
